import React from 'react';
import { styled } from '@mui/material';

import { TeamAnalytics, WorkloadAnalytics, IndividualAnalytics } from 'components/molecules/Analytics';
import { TaskStatistics, TeamInformation } from 'components/organisms/Dashboard';
import { useTask } from 'contexts/task';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import { Box, Stack, Text } from 'shell/components';
import { getAnalyticsStats, getStatus } from 'utils/helper';

const GroupDetailDashboard = () => {
    const { taskList, subtaskList } = useTask();
    const { course, groups, currentGroup, currentGroupAccountUsers, distribution } = useUser2();
    const { device } = useViewport();

    const stats = [...(currentGroupAccountUsers || []), { owner_account_id: '' }].reduce((acc, cur) => {
        return {
            ...acc,
            [cur.owner_account_id || 'unassigned']: getAnalyticsStats(
                [...Object.values(taskList.all), ...Object.values(subtaskList)],
                cur.owner_account_id
            ),
        };
    }, {});

    return device === 'mobile' ? (
        <Stack spacing={4}>
            <MobileBox>
                <TeamInformation
                    groupName={currentGroup?.name}
                    courseName={course.name}
                    members={currentGroupAccountUsers || []}
                    status={getStatus(distribution, groups, currentGroup?.id)}
                />
            </MobileBox>
            <MobileBox>
                <TaskStatistics />
            </MobileBox>
            <MobileBox>
                <MobileAnalyticsBox>
                    <Text variant={'sh2'}>Overall Team Progress</Text>
                    <TeamAnalytics />
                </MobileAnalyticsBox>
            </MobileBox>
            <MobileBox>
                <MobileAnalyticsBox>
                    <Text variant={'sh2'}>Individual Student Progress</Text>
                    <IndividualAnalytics students={currentGroupAccountUsers || []} stats={stats} />
                </MobileAnalyticsBox>
            </MobileBox>
            <MobileBox>
                <MobileAnalyticsBox>
                    <Text variant={'sh2'}>Workload Distribution</Text>
                    <WorkloadAnalytics sx={{ pb: 8 }} students={currentGroupAccountUsers || []} stats={stats} />
                </MobileAnalyticsBox>
            </MobileBox>
        </Stack>
    ) : (
        <RootBox>
            <TeamInformation
                groupName={currentGroup?.name}
                courseName={course.name}
                members={currentGroupAccountUsers || []}
                status={getStatus(distribution, groups, currentGroup?.id)}
            />
            <CustomTaskStatistics />
            <AnalyticsBox>
                <Text variant={'sh2'}>Overall Team Progress</Text>
                <TeamAnalytics />
            </AnalyticsBox>
            <AnalyticsBox>
                <Text variant={'sh2'}>Individual Student Progress</Text>
                <IndividualAnalytics students={currentGroupAccountUsers || []} stats={stats} />
            </AnalyticsBox>
            <AnalyticsBox>
                <Text variant={'sh2'}>Workload Distribution</Text>
                <WorkloadAnalytics students={currentGroupAccountUsers || []} stats={stats} />
            </AnalyticsBox>
        </RootBox>
    );
};

const MobileBox = styled(Box)(({ theme }) => {
    return {
        margin: theme.spacing(0, -5, 0),
    };
});

const MobileAnalyticsBox = styled(Box)(({ theme }) => {
    return {
        padding: theme.spacing(5),
        background: theme.palette.ensightful.shades[0],
    };
});

const RootBox = styled(Box)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gridTemplateRows: '10.5rem auto',
        gridGap: theme.spacing(3),
        '& > div:nth-child(1), & > div:nth-child(2)': {
            borderRadius: '12px',
        },
    };
});

const CustomTaskStatistics = styled(TaskStatistics)(() => {
    return {
        gridColumn: '2 / 4',
    };
});

const AnalyticsBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        height: 'max(100%, calc(100vh - 64px - 20px - 168px - 12px - 34px - 8px - 16px - 32px))',
        padding: theme.spacing(5),
        background: theme.palette.ensightful.shades[0],
    };
});

export default GroupDetailDashboard;
